// Structured logger for server-side code (SSR, API routes, middleware).
// Logs reach Dash0 via the Vercel log drain, which parses single-line JSON
// bodies and promotes `message`, `level`, `trace_id`/`span_id` and nested
// `err: { type, message, stack }` into the log record.
// https://www.dash0.com/documentation/dash0/telemetry/logging/json-log-processing
//
// Edge-safe: imports only @opentelemetry/api. Severity is carried twice: by
// the console method (the drain maps console.error → Error, console.warn →
// Warning, stdout → Info) and by the JSON `level` field, which additionally
// distinguishes debug from info when JSON parsing fires.
//
// Some call sites (profile-cache, trip services) also execute in the browser,
// where no log drain exists — there we fall back to readable console output.

// The OTel API is consulted only on the server path (the browser branch of
// emit returns before reaching it).
import { trace } from '@src/utils/observability/otel-trace';
import { getRequestIdentifierAttributes } from '@src/utils/observability/request-identifiers';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type LogAttributes = Record<string, unknown>;

const isDevelopment = process.env.NODE_ENV === 'development';

const LEVEL_WEIGHT: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

// Debug records are dev-time tracing; without a gate they become
// traffic-proportional ingest volume in Dash0. LOG_LEVEL overrides per env.
const configuredLevel = process.env.LOG_LEVEL as LogLevel | undefined;
const minLevel: LogLevel =
  configuredLevel && configuredLevel in LEVEL_WEIGHT ? configuredLevel : isDevelopment ? 'debug' : 'info';

// The drain derives severity from the stream: console.error → Error,
// console.warn → Warning, stdout → Info.
const CONSOLE_WRITER: Record<LogLevel, (typeof console)['log']> = {
  debug: console.log,
  info: console.log,
  warn: console.warn,
  error: console.error,
};

const MAX_ERROR_DEPTH = 4;

// Vercel truncates long log lines, and a line cut mid-JSON fails the drain's
// parsing so the whole record degrades to unstructured text. Bounding string
// values (API error bodies, HTML error pages) keeps the line intact.
const MAX_STRING_LENGTH = 2048;

// Values are masked wherever the key names auth material, at any nesting
// depth — this covers serialized error objects too (an AxiosError's own
// enumerable `config.headers` carries the Authorization header). Shared with
// report-error.ts so browser-reported attributes get the same treatment.
export const SENSITIVE_KEY_PATTERN = /token|secret|password|authorization|cookie|api[-_]?key|passcode|credential/i;

// What the clone below produces: values JSON.stringify serializes without
// throwing or silently mangling. `undefined` entries are the one exception —
// stringify drops them, which is the intended fate of functions and symbols.
type JsonSafeValue = string | number | boolean | null | undefined | JsonSafeValue[] | { [key: string]: JsonSafeValue };

// Depth-capped, circular-safe clone applied to all attributes (e.g.
// AxiosError.response holds the request/response pair, which is circular —
// a naive spread would make JSON.stringify throw and lose the whole record).
// `seen` tracks the current path only (values are removed after their subtree
// is cloned), so the same object appearing under two keys serializes twice
// instead of degrading to '[Circular]'.
const cloneSafely = (value: LogAttributes[string], depth: number, seen: WeakSet<object>): JsonSafeValue => {
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined) {
    return value;
  }
  // Functions and symbols have no JSON form; stringify would drop them anyway.
  if (typeof value !== 'object') return undefined;
  if (value instanceof Error) {
    return seen.has(value) ? '[Circular]' : serializeErrorLike(value, depth, seen);
  }
  if (seen.has(value)) return '[Circular]';
  if (depth >= MAX_ERROR_DEPTH) return '[Object]';
  seen.add(value);
  const cloned = Array.isArray(value)
    ? value.map((item) => cloneSafely(item, depth + 1, seen))
    : Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, cloneEntry(key, entry, depth, seen)]));
  seen.delete(value);
  return cloned;
};

const cloneEntry = (key: string, value: LogAttributes[string], depth: number, seen: WeakSet<object>): JsonSafeValue =>
  SENSITIVE_KEY_PATTERN.test(key) ? '[REDACTED]' : cloneSafely(value, depth + 1, seen);

// Keeps what the drain promotes (`type`/`message`/`stack`) plus everything a
// bare { name, message, stack } pick would drop: own enumerable props such as
// AxiosError's response/status or HTTPError's errorCode, and cause chains.
const serializeErrorLike = (error: Error, depth: number, seen: WeakSet<object>): { [key: string]: JsonSafeValue } => {
  const out: { [key: string]: JsonSafeValue } = { type: error.name, message: error.message, stack: error.stack };
  if (depth >= MAX_ERROR_DEPTH) return out;
  seen.add(error);
  for (const [key, entry] of Object.entries(error)) {
    out[key] = cloneEntry(key, entry, depth, seen);
  }
  if (error.cause !== undefined) {
    out.cause = cloneSafely(error.cause, depth + 1, seen);
  }
  seen.delete(error);
  return out;
};

// Shared with client-side reporting (report-error.ts) so log records and
// reported-error attributes serialize Errors and circular values identically.
export const toJsonSafe = (value: LogAttributes[string]): JsonSafeValue => cloneSafely(value, 0, new WeakSet());

// The reserved `err` key always exposes the { type, message } shape the drain
// promotes, even for non-Error throwables; plain-object values keep their
// fields instead of collapsing to String(value).
const normalizeErr = (cause: unknown): Error | Record<string, unknown> => {
  if (cause instanceof Error) return cause;
  if (typeof cause === 'object' && cause !== null && !Array.isArray(cause)) {
    return { type: 'UnknownError', ...cause };
  }
  return { type: 'UnknownError', message: String(cause) };
};

const emit = (level: LogLevel, message: string, attributes?: LogAttributes) => {
  if (LEVEL_WEIGHT[level] < LEVEL_WEIGHT[minLevel]) return;

  const write = CONSOLE_WRITER[level];

  // No log drain in dev or in the browser — keep output readable instead of JSON lines.
  if (isDevelopment || typeof window !== 'undefined') {
    write(`[${level}] ${message}`, attributes ?? '');
    return;
  }

  const spanContext = trace?.getActiveSpan()?.spanContext();

  const recordFields: LogAttributes = {
    level,
    message,
    ...(spanContext && { trace_id: spanContext.traceId, span_id: spanContext.spanId }),
  };

  const withErr =
    attributes && 'err' in attributes && attributes.err !== undefined
      ? { ...attributes, err: normalizeErr(attributes.err) }
      : attributes;

  // All attributes go through the clone: Errors JSON.stringify to {} (their
  // props are non-enumerable) and circular values would throw and lose the
  // whole record — wherever they appear, not only under `err`. Identity
  // attributes (user.id, numa.*) come first so call-site attributes can
  // override them; record fields come last so attribute objects (e.g. a
  // passed-through error body containing `message` or `level`) can never
  // clobber them.
  const record: LogAttributes = {
    ...getRequestIdentifierAttributes(),
    ...(withErr && (cloneSafely(withErr, 0, new WeakSet()) as { [key: string]: JsonSafeValue })),
    ...recordFields,
  };

  let line: string;
  try {
    line = JSON.stringify(record);
  } catch {
    // Record fields alone are guaranteed to serialize (the ids are plain hex
    // strings) — the fallback record stays joined to its trace.
    line = JSON.stringify({ ...recordFields, serialization_error: true });
  }

  write(line);
};

// A bare Error is accepted as shorthand for { err: error }; anything else the
// caller wants logged goes under a named attribute key (e.g. { err: cause }),
// so records stay queryable instead of carrying an anonymous payload.
const toAttributes = (data?: LogAttributes | Error): LogAttributes | undefined => {
  if (data === undefined) return undefined;
  if (data instanceof Error) return { err: data };
  return data;
};

export const logger = {
  debug: (message: string, data?: LogAttributes | Error) => emit('debug', message, toAttributes(data)),
  info: (message: string, data?: LogAttributes | Error) => emit('info', message, toAttributes(data)),
  warn: (message: string, data?: LogAttributes | Error) => emit('warn', message, toAttributes(data)),
  error: (message: string, data?: LogAttributes | Error) => emit('error', message, toAttributes(data)),
};
